import { AxiosErrorResponse } from '@/@types/Errors'
import type {
  RegulatoryDocumentResponse,
  RegulatoryDocumentUploadItem,
} from '@/@types/Products'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import { Input } from '@/components/shared/Input/Input'
import DeliverableCard from '@/components/Products/DeliverableCard'
import {
  getRegulatoryDocumentDisplayName,
  getRegulatoryDocumentFileUrl,
  getRegulatoryDocumentSizeLabel,
  getRegulatoryDocumentStatusTranslationKey,
  mapRegulatoryDocumentBadgeStatus,
} from '@/components/Products/regulatory-document-utils'
import RenderSkeleton from '@/components/Products/edit-product-skeleton'
import {
  getRegulatoryDocumentTypeOptions,
  REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES,
} from '@/components/Products/regulatory-document-types'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/hooks/use-toast'
import { convertBytesToMB } from '@/lib/select-options'
import { ProductsService } from '@/services/products.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Loader2, Plus, UploadCloudIcon } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface RegulatoryDocumentsSectionProps {
  productId: string
  canUpload: boolean
  canDelete: boolean
  isLoadingProductDetail?: boolean
}

type DraftDocument = {
  file: File | null
  name: string
  type: string
  notes: string
}

const emptyDraft = (): DraftDocument => ({
  file: null,
  name: '',
  type: '',
  notes: '',
})

type HighlightedRegulatoryDocumentType = typeof REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES[number]

export default function RegulatoryDocumentsSection({
  productId,
  canUpload,
  canDelete,
  isLoadingProductDetail = false,
}: RegulatoryDocumentsSectionProps) {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()
  const typeOptions = getRegulatoryDocumentTypeOptions(t)

  const [draft, setDraft] = useState<DraftDocument>(emptyDraft)

  const queryKey = [`list-regulatory-documents-${productId}`]

  const { data: documents = [], isPending: isLoadingDocuments } = useQuery({
    queryKey,
    queryFn: () => ProductsService.listRegulatoryDocuments(productId),
    enabled: Boolean(productId),
  })

  const { mutate: uploadDocuments, isPending: isUploading } = useMutation({
    mutationFn: (items: RegulatoryDocumentUploadItem[]) =>
      ProductsService.uploadRegulatoryDocuments({
        productId,
        documents: items,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      setDraft(emptyDraft())
      toast({
        variant: 'success',
        description: t('regulatoryDocumentsUploadSuccess'),
      })
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('data_submission_error'),
      })
    },
  })

  const { mutate: deleteDocument } = useMutation({
    mutationFn: (documentId: number) =>
      ProductsService.deleteRegulatoryDocument({
        productId,
        documentId: String(documentId),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      toast({
        variant: 'success',
        description: t('file_successfully_deleted'),
      })
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('data_submission_error'),
      })
    },
  })

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (file.size > 50 * 1024 * 1024) {
        toast({ variant: 'error', description: t('file_max_50MB') })
        return
      }

      if (file.type !== 'application/pdf') {
        toast({ variant: 'error', description: t('only_pdf_files_allowed') })
        return
      }

      setDraft((prev) => ({
        ...prev,
        file,
        name: prev.name.trim() ? prev.name : file.name.replace(/\.pdf$/i, ''),
      }))
      e.target.value = ''
    },
    [t],
  )

  const handleUploadDraft = () => {
    if (!draft.file) {
      toast({
        variant: 'error',
        description: t('regulatoryDocumentFileRequired'),
      })
      return
    }
    if (!draft.name.trim()) {
      toast({
        variant: 'error',
        description: t('regulatoryDocumentNameRequired'),
      })
      return
    }

    uploadDocuments([
      {
        file: draft.file,
        document_name: draft.name.trim(),
        document_type: draft.type || undefined,
        note: draft.notes.trim() || undefined,
      },
    ])
  }

  const highlightedSlots: Array<{
    index: number
    type: HighlightedRegulatoryDocumentType | 'other'
    title: string
    description: string
  }> = [
    {
      index: 1,
      type: 'contract',
      title: t('regulatoryDocumentsSlotContractTitle', {
        defaultValue: 'Contrato com fabricante',
      }),
      description: t('regulatoryDocumentsSlotContractDescription', {
        defaultValue:
          'Envie o contrato assinado com o fabricante, fulfillment ou responsável pela fabricação e entrega do produto.',
      }),
    },
    {
      index: 2,
      type: 'coa',
      title: t('regulatoryDocumentsSlotCoaTitle', {
        defaultValue: 'COA',
      }),
      description: t('regulatoryDocumentsSlotCoaDescription', {
        defaultValue: 'Certificado de análise do produto.',
      }),
    },
    {
      index: 3,
      type: 'label',
      title: t('regulatoryDocumentsSlotLabelTitle', {
        defaultValue: 'Rótulo do produto com ingredientes',
      }),
      description: t('regulatoryDocumentsSlotLabelDescription', {
        defaultValue:
          'Envie a imagem do rótulo com a composição e ingredientes.',
      }),
    },
    {
      index: 4,
      type: 'other',
      title: t('regulatoryDocumentsSlotOtherTitle', {
        defaultValue: 'Documentos extras',
      }),
      description: t('regulatoryDocumentsSlotOtherDescription', {
        defaultValue: 'Você pode enviar 1 ou mais documentos complementares.',
      }),
    },
  ]

  const highlightedTypeSet = new Set<string>(
    REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES,
  )

  const groupedHighlightedDocuments = highlightedSlots
    .map((slot) => ({
      ...slot,
      documents:
        slot.type === 'other'
          ? documents.filter(
              (doc) => !highlightedTypeSet.has(doc.document_type ?? ''),
            )
          : documents.filter((doc) => doc.document_type === slot.type),
    }))
    .filter((slot) => slot.documents.length > 0)

  const renderDocumentItem = (
    doc: RegulatoryDocumentResponse,
    showTypeInMeta = true,
  ) => {
    const typeLabel = doc.document_type
      ? typeOptions.find((o) => o.value === doc.document_type)?.label ??
        doc.document_type
      : null

    const meta = [showTypeInMeta ? typeLabel : null, doc.note]
      .filter(Boolean)
      .join(' · ')

    return (
      <DeliverableCard
        key={doc.id}
        id={productId}
        deliverableName={getRegulatoryDocumentDisplayName(doc)}
        deliverableSize={getRegulatoryDocumentSizeLabel(doc)}
        internalNote={doc.internal_note}
        url={getRegulatoryDocumentFileUrl(doc)}
        meta={meta || undefined}
        badgeStatus={
          doc.status ? mapRegulatoryDocumentBadgeStatus(doc.status) : undefined
        }
        badgeText={
          doc.status
            ? t(
                `regulatoryDocumentStatus_${getRegulatoryDocumentStatusTranslationKey(
                  doc.status,
                )}`,
              )
            : undefined
        }
        onDelete={() => {
          if (canDelete) deleteDocument(doc.id)
        }}
      />
    )
  }

  return (
    <section className="gap-6 w-full flex mt-12 flex-col lg:flex-row">
      <article className="space-y-1 lg:w-[328px]">
        <h2 className="text-colorText font-medium">
          {t('regulatoryDocumentsTitle')}
        </h2>
        <p className="text-muted text-sm">{t('regulatoryDocumentsSubtitle')}</p>
      </article>

      <div className="w-full lg:max-w-[680px]">
        <Card className="w-full p-4 lg:p-6 bg-[09090b] space-y-6">
          {isLoadingProductDetail ? (
            <RenderSkeleton count={3} />
          ) : (
            <>
              {canUpload && (
                <div className="space-y-4">
                  <label
                    htmlFor="regulatory-document-file"
                    className="custom-upload-label flex flex-col cursor-pointer items-center justify-center"
                    data-cy="regulatory-document-input"
                  >
                    <UploadCloudIcon
                      width={24}
                      height={24}
                      className="upload-icon text-[#52525B] text-center"
                    />
                    <span className="upload-text mt-4 text-center">
                      {t('click_to_upload')}
                    </span>
                    <span className="upload-text text-center">
                      {t('pdf_up_to_50MB')}
                    </span>
                    <input
                      id="regulatory-document-file"
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                  </label>

                  {draft.file && (
                    <DeliverableCard
                      id={productId}
                      deliverableName={draft.file.name}
                      deliverableSize={convertBytesToMB(draft.file.size)}
                      onDelete={() => setDraft(emptyDraft())}
                    />
                  )}

                  <Input
                    label={t('regulatoryDocumentName')}
                    placeholder={t('regulatoryDocumentNamePlaceholder')}
                    value={draft.name}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, name: e.target.value }))
                    }
                    disabled={!canUpload || isUploading}
                  />

                  <CustomSelect
                    label={t('regulatoryDocumentType')}
                    placeholder={t('regulatoryDocumentTypePlaceholder')}
                    options={typeOptions}
                    value={draft.type || undefined}
                    onValueChange={(value) =>
                      setDraft((prev) => ({ ...prev, type: value }))
                    }
                  />

                  <div className="flex flex-col gap-2">
                    <Label>{t('regulatoryDocumentNotes')}</Label>
                    <Textarea
                      placeholder={t('regulatoryDocumentNotesPlaceholder')}
                      value={draft.notes}
                      onChange={(e) =>
                        setDraft((prev) => ({ ...prev, notes: e.target.value }))
                      }
                      className="min-h-[80px] border-colorBorder"
                      disabled={!canUpload || isUploading}
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleUploadDraft}
                    disabled={!canUpload || isUploading || !draft.file}
                    data-cy="regulatory-document-upload-button"
                  >
                    {isUploading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('saving')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        {t('regulatoryDocumentAdd')}
                      </span>
                    )}
                  </Button>
                </div>
              )}

              {!isLoadingDocuments && documents.length > 0 && (
                <div className="space-y-2">
                  <Label>{t('loaded_files_already')}</Label>
                  {groupedHighlightedDocuments.map((slot) => (
                    <div
                      key={slot.type}
                      className="rounded-2xl border border-white/10 bg-[#09090b] p-5"
                    >
                      <div className="flex flex-col gap-4 lg:items-start lg:justify-between">
                        <div className="lg:max-w-[280px]">
                          <div className="mb-3 flex items-center gap-3">                           
                            <h3 className="text-base font-semibold text-white">
                              {slot.title}
                            </h3>
                          </div>
                        </div>

                        <div className="w-full space-y-3">
                          {slot.documents.map((doc) =>
                            renderDocumentItem(doc, slot.type === 'other'),
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </section>
  )
}
